import {
  Server,
  Users,
  GitBranch,
  Target,
  Waypoints,
  FileText,
  Scale,
  ArrowRight,
  Trash2,
} from 'lucide-react';
import { Badge, Card, CardBody } from '../../ui';
import type { ServiceSummary } from '../../features/services/types';

export interface ServiceCardProps {
  service: ServiceSummary;
  onOpen?: (id: string) => void;
  onDelete?: (service: ServiceSummary) => void;
}

/** A count chip for a service's connected entities. */
function CountChip({
  icon: Icon,
  label,
  count,
}: {
  icon: typeof Target;
  label: string;
  count: number;
}) {
  if (count <= 0) return null;
  return (
    <Badge tone="neutral">
      <Icon className="h-3 w-3" /> {count} {label}
    </Badge>
  );
}

/**
 * A single Service Directory card — the "where do I start / who owns this?"
 * summary, with connection counts and the primary POC surfaced.
 */
export function ServiceCard({ service, onOpen, onDelete }: ServiceCardProps) {
  return (
    <Card interactive={!!onOpen} onClick={onOpen ? () => onOpen(service.id) : undefined}>
      <CardBody className="space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-start gap-2">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-surface-3 text-accent">
              <Server className="h-4 w-4" aria-hidden="true" />
            </div>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-zinc-100">
                {service.name || 'Unnamed service'}
              </p>
              {service.purpose && (
                <p className="mt-0.5 line-clamp-2 text-xs text-zinc-400">{service.purpose}</p>
              )}
            </div>
          </div>
          {onDelete && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(service);
              }}
              aria-label="Delete service"
              className="shrink-0 rounded-md p-1 text-zinc-500 transition-colors hover:bg-surface-3 hover:text-red-300"
            >
              <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-zinc-500">
          {service.owningTeam && (
            <span>
              Team: <span className="text-zinc-300">{service.owningTeam}</span>
            </span>
          )}
          {service.primaryPoc && (
            <span className="inline-flex items-center gap-1">
              <Users className="h-3 w-3" aria-hidden="true" />
              <span className="text-zinc-300">{service.primaryPoc}</span>
              {service.secondaryPoc ? ` · ${service.secondaryPoc}` : ''}
            </span>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-1.5">
          <CountChip icon={GitBranch} label="repos" count={service.repositories.length} />
          <CountChip icon={Target} label="goals" count={service.currentGoalIds.length} />
          <CountChip icon={Waypoints} label="workstreams" count={service.activeWorkstreamIds.length} />
          <CountChip icon={FileText} label="docs" count={service.documentIds.length} />
          <CountChip icon={Scale} label="decisions" count={service.recentDecisionIds.length} />
        </div>

        {onOpen && (
          <div className="flex items-center justify-end">
            <ArrowRight className="h-4 w-4 text-zinc-600" aria-hidden="true" />
          </div>
        )}
      </CardBody>
    </Card>
  );
}

export default ServiceCard;
